import { useEffect, useState } from "react";
import { X, Share, Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Unified PWA install banner.
 *
 * - On Android Chrome (and other browsers that fire `beforeinstallprompt`),
 *   we capture the event and show a one-tap "Install" button. Tapping it
 *   triggers the native browser install dialog.
 * - On iOS Safari, where there's no programmatic install API, we show a
 *   "Share -> Add to Home Screen" hint so users know how to install.
 * - When the app is already installed (standalone), the banner is hidden.
 * - When the user dismisses the banner, we remember that for ~7 days.
 */
const DISMISS_KEY = "ncl_install_hint_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function InstallHintBanner() {
  const { t, lang } = useI18n();
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState(null); // "android" | "ios"
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Dismissed recently?
    try {
      const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (at && Date.now() - at < DISMISS_TTL_MS) return;
    } catch (_) {}

    // Already installed?
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua);

    // Android (Chrome / Samsung Internet / Edge / Brave) fire this event.
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode("android");
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS gets the manual hint immediately.
    if (isIOS) {
      setMode("ios");
      setShow(true);
    }

    const onInstalled = () => {
      setShow(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setShow(false);
  };

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (choice.outcome === "accepted") {
        setShow(false);
      }
    } catch (_) {
      // ignore
    } finally {
      setInstalling(false);
    }
  };

  if (!show) return null;

  const isAndroid = mode === "android";

  const title = isAndroid
    ? (lang === "es" ? "Instala la app en tu telefono" : "Install the app on your phone")
    : t.home.installHintTitle;
  const body = isAndroid
    ? (lang === "es"
        ? "Tenla siempre a la mano. Toca Instalar y se anadira como una app normal."
        : "Keep it close. Tap Install and it'll be added like a regular app.")
    : t.home.installHintBody;

  return (
    <div className="rounded-2xl border border-primary/20 bg-white p-4 shadow-sm ring-1 ring-black/5" data-testid="pwa-install-hint">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
          {isAndroid ? <Download className="h-4 w-4" /> : <Share className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-serif-display text-base font-semibold leading-tight">{title}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{body}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {isAndroid ? (
              <>
                <Button size="sm" onClick={triggerInstall} disabled={installing} data-testid="pwa-install-android">
                  <Download className="mr-1 h-4 w-4" />
                  {installing ? "..." : (lang === "es" ? "Instalar" : "Install")}
                </Button>
                <Button size="sm" variant="outline" onClick={dismiss} data-testid="pwa-install-dismiss">
                  {lang === "es" ? "Ahora no" : "Not now"}
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={dismiss} data-testid="pwa-install-dismiss">
                {t.home.installDismiss}
              </Button>
            )}
          </div>
        </div>
        <button type="button" onClick={dismiss} aria-label="close" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
